import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Snackbar, Alert, Typography,
} from '@mui/material';
import { formatBRL } from '../../../UI';
import TableNovoAcordo from './TableNovoAcordo';
import {
  findDadosDefaultAcordo, findResumoAcordo,
} from '../../../../../services/core/processos/processosAcordosService';
import {
  findIdsTitulosService, findNovoAcordosService,
} from '../../../../../services/core/processos/processosTitulosService';
import { diferenca } from '../../../../../helpers/utils/dataStructure.utils';
import { toBrazilianDate } from '../../../../../helpers/utils/utils';

const MENSAGEM_ERRO_TITULOS = 'Não foi possível carregar os títulos do processo.';
const MENSAGEM_ERRO_PRESELECAO = 'Não foi possível pré-selecionar os títulos do processo.';
const MENSAGEM_ERRO_AVANCAR = 'Não foi possível carregar os dados do acordo.';

const COLUNAS = [
  {
    id: 'sequencia', label: 'Sequência', align: 'center', sortable: true,
  },
  { id: 'idTipoTitulo', label: 'Tipo', align: 'center' },
  { id: 'numero', label: 'Número', align: 'center' },
  {
    id: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true,
  },
  {
    id: 'atraso', label: 'Atraso', align: 'center', sortable: true,
  },
  { id: 'valorOriginal', label: 'Valor Original', align: 'right' },
  {
    id: 'valorSaldoOriginal', label: 'Saldo do Original', align: 'right', sortable: true,
  },
];

function formatarTitulo(titulo) {
  return {
    ...titulo,
    dataVencimento: toBrazilianDate(titulo.dataVencimento),
    valorOriginal: formatBRL(titulo.valorOriginal),
    valorSaldoOriginal: formatBRL(titulo.valorSaldoOriginal),
  };
}

/**
 * Passo 1 de Novo Acordo — port de `NovoAcordoPassoUm.jsx` real.
 *
 * Regras replicadas fielmente da tela real:
 * - ao montar, pré-seleciona todos os títulos retornados por
 *   `findIdsTitulosService`; numa nova busca, só os ids que ainda não
 *   apareciam antes são marcados automaticamente (diff via `diferenca`) —
 *   a seleção manual do usuário em ids já conhecidos nunca é sobrescrita.
 * - "Próximo" dispara os 2 fetches de dados do acordo e avança de passo
 *   IMEDIATAMENTE, sem esperar as respostas (mesmo comportamento da tela
 *   real) — o Passo 2 (Etapa 5 do plano) é quem lida com o carregamento.
 */
export default function NovoAcordoPassoUm({
  idProcesso, selectedIds, setSelectedIds, listaDeIds, setDadosDefault, setDadosResumo, onAvancar, onVoltar,
}) {
  const [titulos, setTitulos] = useState([]);
  const [ordenacao, setOrdenacao] = useState({ element: 'sequencia', order: 'ASC' });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // "Latest ref" — mesmo padrão de AbaAcompanhamento.jsx/BarraAcoesProcesso.jsx:
  // NovoAcordo (pai) preserva selectedIds/dadosDefault/dadosResumo por
  // processo sem desmontar este componente ao trocar de idProcesso (o painel
  // "aberto" é persistido por processo em BotaoNovoAcordo). Toda resolução
  // assíncrona precisa confirmar que o processo-alvo ainda é o atual antes de
  // aplicar o resultado, senão dado do processo errado vaza para a tela (e,
  // pior, para o payload de findResumoAcordo).
  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  const pesquisarTitulos = useCallback((novaOrdenacao) => {
    const alvo = idProcesso;
    setCarregando(true);
    findNovoAcordosService(alvo, [`${novaOrdenacao.element},${novaOrdenacao.order}`])
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setTitulos(data || []);
        }
      })
      .catch(() => {
        if (idProcessoRef.current === alvo) {
          setErro(MENSAGEM_ERRO_TITULOS);
        }
      })
      .finally(() => {
        if (idProcessoRef.current === alvo) {
          setCarregando(false);
        }
      });
  }, [idProcesso]);

  useEffect(() => {
    setTitulos([]);
    pesquisarTitulos(ordenacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProcesso]);

  useEffect(() => {
    const alvo = idProcesso;
    findIdsTitulosService(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current !== alvo) {
          return;
        }
        setSelectedIds((selecaoAnterior) => {
          const semSelecaoAtual = Object.keys(selecaoAnterior).every(id => selecaoAnterior[id] !== true);
          if (semSelecaoAtual) {
            return data.reduce((acumulador, id) => ({ ...acumulador, [id]: true }), {});
          }
          const idsAtuais = new Set(Object.keys(selecaoAnterior).map(Number));
          const idsRecemChegados = diferenca(new Set(data), idsAtuais);
          if (idsRecemChegados.size === 0) {
            return selecaoAnterior;
          }
          const novosSelecionados = [...idsRecemChegados].reduce((acumulador, id) => ({ ...acumulador, [id]: true }), {});
          return { ...selecaoAnterior, ...novosSelecionados };
        });
      })
      .catch(() => {
        if (idProcessoRef.current === alvo) {
          setErro(MENSAGEM_ERRO_PRESELECAO);
        }
      });
  }, [idProcesso, setSelectedIds]);

  const onClickColuna = (colunaId) => {
    const novaOrdem = ordenacao.element === colunaId && ordenacao.order === 'ASC' ? 'DESC' : 'ASC';
    const novaOrdenacao = { element: colunaId, order: novaOrdem };
    setOrdenacao(novaOrdenacao);
    pesquisarTitulos(novaOrdenacao);
  };

  const onToggleId = (idTitulo) => {
    setSelectedIds(selecaoAtual => ({ ...selecaoAtual, [idTitulo]: !selecaoAtual[idTitulo] }));
  };

  const todosSelecionados = titulos.length > 0 && titulos.every(titulo => selectedIds[titulo.idTitulo]);

  const onToggleTodos = (event) => {
    const { checked } = event.target;
    setSelectedIds(selecaoAtual => titulos.reduce(
      (acumulador, titulo) => ({ ...acumulador, [titulo.idTitulo]: checked }),
      selecaoAtual,
    ));
  };

  const pesquisarDadosDefault = useCallback(() => {
    const alvo = idProcesso;
    findDadosDefaultAcordo(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setDadosDefault(data);
        }
      })
      .catch(() => {
        if (idProcessoRef.current === alvo) {
          setErro(MENSAGEM_ERRO_AVANCAR);
        }
      });
  }, [idProcesso, setDadosDefault]);

  const pesquisarDadosResumo = useCallback(() => {
    const alvo = idProcesso;
    findResumoAcordo(alvo, listaDeIds)
      .then(({ data }) => {
        if (idProcessoRef.current !== alvo) {
          return;
        }
        setDadosResumo({ ...data, jsonErros: JSON.parse(data.jsonErros)[0] });
      })
      .catch(() => {
        if (idProcessoRef.current === alvo) {
          setErro(MENSAGEM_ERRO_AVANCAR);
        }
      });
  }, [idProcesso, listaDeIds, setDadosResumo]);

  const onClickProximo = () => {
    pesquisarDadosDefault();
    pesquisarDadosResumo();
    onAvancar();
  };

  const titulosFormatados = titulos.map(formatarTitulo);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Selecione os Títulos que deseja incluir no Acordo ou Simulação. Somente Títulos que não fazem parte de outro Acordo estão listados abaixo:
      </Typography>
      {carregando ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>Carregando títulos...</Typography>
      ) : (
        <TableNovoAcordo
          colunas={COLUNAS}
          titulos={titulosFormatados}
          selectedIds={selectedIds}
          onToggleId={onToggleId}
          todosSelecionados={todosSelecionados}
          onToggleTodos={onToggleTodos}
          ordenacao={ordenacao}
          onClickColuna={onClickColuna}
        />
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button onClick={onVoltar}>Voltar</Button>
        <Button
          variant="contained"
          disabled={listaDeIds.length === 0}
          onClick={onClickProximo}
        >
          Próximo
        </Button>
      </Box>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

NovoAcordoPassoUm.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedIds: PropTypes.object,
  setSelectedIds: PropTypes.func.isRequired,
  listaDeIds: PropTypes.array,
  setDadosDefault: PropTypes.func.isRequired,
  setDadosResumo: PropTypes.func.isRequired,
  onAvancar: PropTypes.func.isRequired,
  onVoltar: PropTypes.func.isRequired,
};

NovoAcordoPassoUm.defaultProps = {
  idProcesso: null,
  selectedIds: {},
  listaDeIds: [],
};
